"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.tickLabelClick = exports.chartClick = exports.getClickableElementNameByChartType = void 0;
var tslib_1 = require("tslib");
// (C) 2007-2021 GoodData Corporation
var debounce_1 = tslib_1.__importDefault(require("lodash/debounce"));
var cloneDeep_1 = tslib_1.__importDefault(require("lodash/cloneDeep"));
var custom_event_1 = tslib_1.__importDefault(require("custom-event"));
var ts_invariant_1 = tslib_1.__importDefault(require("ts-invariant"));
var sdk_ui_1 = require("@gooddata/sdk-ui");
var common_1 = require("../_util/common");
var isGroupHighchartsDrillEvent_1 = require("./isGroupHighchartsDrillEvent");
function getClickableElementNameByChartType(type) {
    switch (type) {
        case sdk_ui_1.VisualizationTypes.LINE:
        case sdk_ui_1.VisualizationTypes.AREA:
        case sdk_ui_1.VisualizationTypes.SCATTER:
        case sdk_ui_1.VisualizationTypes.BUBBLE:
            return "point";
        case sdk_ui_1.VisualizationTypes.COLUMN:
        case sdk_ui_1.VisualizationTypes.BAR:
            return "bar";
        case sdk_ui_1.VisualizationTypes.PIE:
        case sdk_ui_1.VisualizationTypes.TREEMAP:
        case sdk_ui_1.VisualizationTypes.DONUT:
        case sdk_ui_1.VisualizationTypes.FUNNEL:
            return "slice";
        case sdk_ui_1.VisualizationTypes.HEATMAP:
            return "cell";
        default:
            ts_invariant_1.default(false, "Unknown visualization type: " + type);
            return null;
    }
}
exports.getClickableElementNameByChartType = getClickableElementNameByChartType;
function fireEvent(onDrill, data, target) {
    var returnValue = onDrill(data);
    // if user-specified onDrill fn returns false, do not fire default DOM event
    if (returnValue !== false) {
        var event_1 = new custom_event_1.default("drill", {
            detail: data,
            bubbles: true,
        });
        target.dispatchEvent(event_1);
    }
}
var getElementChartType = function (chartType, point) {
    var _a, _b;
    return (_b = (_a = point === null || point === void 0 ? void 0 : point.series) === null || _a === void 0 ? void 0 : _a.type) !== null && _b !== void 0 ? _b : chartType;
};
var getDrillPointCustomProps = function (point, chartType) {
    var _a, _b, _c;
    if (common_1.isComboChart(chartType)) {
        return { type: (_a = point === null || point === void 0 ? void 0 : point.series) === null || _a === void 0 ? void 0 : _a.type };
    }
    if (common_1.isBulletChart(chartType)) {
        return { type: (_c = (_b = point === null || point === void 0 ? void 0 : point.series) === null || _b === void 0 ? void 0 : _b.userOptions) === null || _c === void 0 ? void 0 : _c.bulletChartMeasureType };
    }
    return {};
};
var getYValueForBulletChartTarget = function (point) {
    if (point.isNullTarget) {
        return null;
    }
    return point.target;
};
function composeDrillContextGroup(points, chartType) {
    var sanitizedPoints = sanitizeContextPoints(chartType, points);
    var contextPoints = sanitizedPoints.map(function (point) {
        var elementChartType = getElementChartType(chartType, point);
        var customProps = getDrillPointCustomProps(point, chartType);
        return tslib_1.__assign({ x: point.x, y: elementChartType === "bullet" ? getYValueForBulletChartTarget(point) : point.y, intersection: point.drillIntersection }, customProps);
    });
    return {
        type: chartType,
        element: "label",
        points: contextPoints,
    };
}
function getClickableElementNameForBulletChart(point) {
    return point.series.userOptions.bulletChartMeasureType;
}
function composeDrillContextPoint(point, chartType) {
    var zProp = isNaN(point.z) ? {} : { z: point.z };
    var valueProp = common_1.isTreemap(chartType) || common_1.isHeatmap(chartType)
        ? {
            value: point.value ? point.value.toString() : "",
        }
        : {};
    var elementChartType = getElementChartType(chartType, point);
    var xyProp = common_1.isTreemap(chartType)
        ? {}
        : {
            x: point.x,
            y: elementChartType === "bullet" ? point.target : point.y,
        };
    var customProp = common_1.isComboChart(chartType)
        ? {
            elementChartType: elementChartType,
        }
        : {};
    return tslib_1.__assign(tslib_1.__assign(tslib_1.__assign(tslib_1.__assign({ type: chartType, element: common_1.isBulletChart(chartType)
            ? getClickableElementNameForBulletChart(point)
            : getClickableElementNameByChartType(elementChartType), intersection: point.drillIntersection }, xyProp), zProp), valueProp), customProp);
}
var chartClickDebounced = debounce_1.default(function (drillConfig, event, target, chartType) {
    var dataView = drillConfig.dataView, onDrill = drillConfig.onDrill;
    var type = sdk_ui_1.getVisualizationType(chartType);
    var drillContext;
    if (isGroupHighchartsDrillEvent_1.isGroupHighchartsDrillEvent(event)) {
        var points = event.points;
        drillContext = composeDrillContextGroup(points, type);
    }
    else {
        var point = event.point;
        drillContext = composeDrillContextPoint(point, type);
    }
    var data = {
        dataView: dataView,
        drillContext: drillContext,
    };
    fireEvent(onDrill, data, target);
});
function chartClick(drillConfig, event, target, chartType) {
    chartClickDebounced(drillConfig, event, target, chartType);
}
exports.chartClick = chartClick;
var tickLabelClickDebounce = debounce_1.default(function (drillConfig, points, target, chartType) {
    var dataView = drillConfig.dataView, onDrill = drillConfig.onDrill;
    var sanitizedPoints = sanitizeContextPoints(chartType, points);
    var contextPoints = sanitizedPoints.map(function (point) {
        var _a, _b;
        var customProps = common_1.isBulletChart(chartType)
            ? { type: (_b = (_a = point === null || point === void 0 ? void 0 : point.series) === null || _a === void 0 ? void 0 : _a.userOptions) === null || _b === void 0 ? void 0 : _b.bulletChartMeasureType }
            : {};
        return tslib_1.__assign({ x: point.x, y: point.y, intersection: cloneDeep_1.default(point.drillIntersection) }, customProps);
    });
    var drillContext = {
        type: chartType,
        element: "label",
        points: contextPoints,
    };
    var data = {
        dataView: dataView,
        drillContext: drillContext,
    };
    fireEvent(onDrill, data, target);
});
function sanitizeContextPoints(chartType, points) {
    if (common_1.isHeatmap(chartType)) {
        return points.filter(function (point) { return !point.ignoredInDrillEventContext; });
    }
    return points;
}
function tickLabelClick(drillConfig, points, target, chartType) {
    tickLabelClickDebounce(drillConfig, points, target, chartType);
}
exports.tickLabelClick = tickLabelClick;
//# sourceMappingURL=drilldownEventing.js.map